import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiHttp } from '@core/api/api-http';
import {
  LeadSearchFilterOptions,
  LeadService
} from '@features/leads/services/lead.service';
import { LibraryService } from '@features/library/services/library.service';
import { OpportunityListItem } from '@features/opportunities/models/opportunity';
import { OpportunityService } from '@features/opportunities/services/opportunity.service';
import { ConsolidatedTask } from '@features/tasks/models/task';
import { TaskTrackingStatusPipe } from '@features/tasks/pipes/task-tracking-status.pipe';
import { ConsolidatedTaskService } from '@features/tasks/services/consolidated-task.service';
import { Status } from '@shared/enums/status.enum';
import { TaskTracking } from '@shared/models/tasks';

import { Activity } from '../models/activity';
import { Announcement } from '../models/announcement';
import { Insight } from '../models/insight';
import { Tasks } from '../models/tasks';
import { Lead } from '@features/leads/models/lead';
import { OpportunityApiHttp } from '@core/api/opportunity-api-http';

const mockLendingActivity: Activity[] = [
  { title: 'home loan', value: 0, icon: 'house' },
  { title: 'car loan', value: 0, icon: 'car' },
  { title: 'business loan', value: 0, icon: 'briefcase' },
  { title: 'commerical loan', value: 0, icon: 'building' },
  { title: 'smsf loan', value: 0, icon: 'donate' },
  { title: 'personal loan', value: 0, icon: 'palm' }
];

@Injectable({
  providedIn: 'root'
})
export class DashboardService {
  constructor(
    private http: OpportunityApiHttp,
    private consolidatedTaskService: ConsolidatedTaskService,
    private opportunityService: OpportunityService,
    private libraryService: LibraryService,
    private leadService: LeadService
  ) {}

  public getTaskOverview(): Observable<Tasks> {
    return this.consolidatedTaskService
      .getTasks(true)
      .pipe(map((t) => this.getDashboardTaskOverview(t)));
  }

  public getAnnouncements(): Observable<Announcement[]> {
    return this.libraryService.getAnnouncements().pipe(
      map((r) =>
        r.items?.map((i) => ({
          id: i.id,
          name: i.filename,
          url: i.url,
          new: false
        }))
      )
    );
  }

  getOpportunityInsight(): Observable<Insight> {
    return this.http.get<Insight>(`dashboard/opportunity-insight`, {
      headers: ApiHttp.getStandardOptions().headers
    });
  }

  public getLendingActivity(): Observable<Activity[]> {
    // TODO: service
    return of(mockLendingActivity);
  }

  getLoanInsight(): Observable<Insight> {
    return this.http.get<Insight>(`dashboard/loan-insight`, {
      headers: ApiHttp.getStandardOptions().headers
    });
  }

  public getLeadInsight(): Observable<Insight> {
    // TODO: service update to leads-insight endpoint
    const options: LeadSearchFilterOptions = {
      status: Lead.Status.Open,
      search: ''
    };
    return this.leadService.getLeads(options).pipe(
      map((d) => ({
        value: d.length || 0,
        change: 0
      }))
    );
  }

  public getOpportunities(): Observable<OpportunityListItem[]> {
    return this.opportunityService.list({ status: Status.Open, search: null, type: null, createdDateFrom: null, createdDateTo: null, ownedBy: null });
  }

  private getDashboardTaskOverview(tasks: ConsolidatedTask.Model[]) {
    const initialCount: Tasks = {
      dueNow: 0,
      overdue: 0,
      upcoming: 0
    };
    return tasks
      .filter((t) => !t.completed)
      .map((t) => new TaskTrackingStatusPipe().transform(t.due))
      .reduce((r, s) => this.countTaskByTracking(r, s), initialCount);
  }

  private countTaskByTracking(taskOverview: Tasks, s: TaskTracking): Tasks {
    switch (s) {
      case 'now':
        taskOverview.dueNow += 1;
        break;
      case 'overdue':
        taskOverview.overdue += 1;
        break;
      case 'upcoming':
      default:
        taskOverview.upcoming += 1;
    }
    return taskOverview;
  }
}
